import type { LogRead, ReviewLogPort } from '@kc/core/ports'
import { parseCardKey } from '@kc/core/model/ids'
import type { Review } from '@kc/core/review/scheduling'

const DB_NAME = 'kc-review'
const STORE = 'reviews'
const VERSION = 1
const RATINGS = new Set(['again', 'hard', 'good', 'easy'])

function isReview(value: unknown): value is Review {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record.id === 'string' &&
    typeof record.card === 'string' &&
    typeof record.rating === 'string' &&
    RATINGS.has(record.rating) &&
    (record.score === null || typeof record.score === 'number') &&
    typeof record.reviewedOn === 'number' &&
    typeof record.reviewedAtMs === 'number' &&
    typeof record.device === 'string'
  )
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
  })
}

/**
 * reviews.jsonl 的浏览器等价物：一个只追加的对象仓库。
 * 读取时按 reviewedAtMs 排序、按 id 去重；坏记录跳过并在控制台留痕。
 * 如果 IndexedDB 不可用，退回当前进程内存，保证会话还能继续，只是不持久。
 */
export class IndexedDbReviewLog implements ReviewLogPort {
  readonly #memory: Review[] = []
  #db: Promise<IDBDatabase | null> | null = null

  #getDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null)
    if (this.#db === null) this.#db = openDb().catch(() => null)
    return this.#db
  }

  async readAll(): Promise<LogRead> {
    const db = await this.#getDb()
    let values: unknown[] = this.#memory

    if (db !== null) {
      try {
        const tx = db.transaction(STORE, 'readonly')
        values = await requestResult(tx.objectStore(STORE).getAll())
      } catch {
        values = this.#memory
      }
    }

    const byId = new Map<string, Review>()
    let invalid = 0
    for (const value of values) {
      if (!isReview(value)) {
        invalid += 1
        continue
      }
      if (!byId.has(value.id)) byId.set(value.id, value)
    }
    if (invalid > 0) console.warn(`复习日志里有 ${invalid} 条坏记录，已跳过`)

    const reviews = [...byId.values()].sort(
      (a, b) => a.reviewedAtMs - b.reviewedAtMs || a.id.localeCompare(b.id),
    )
    return { reviews, issues: [] }
  }

  async append(review: Review): Promise<void> {
    this.#memory.push(review)

    const db = await this.#getDb()
    if (db === null) return

    try {
      const tx = db.transaction(STORE, 'readwrite')
      await requestResult(tx.objectStore(STORE).add(review))
    } catch (error) {
      // 重放同一 submissionId 时可能撞主键；语义上这条已经记过了。
      if (error instanceof DOMException && error.name === 'ConstraintError') return
      throw error
    }
  }

  async clear(): Promise<void> {
    this.#memory.length = 0
    const db = await this.#getDb()
    if (db === null) return
    try {
      const tx = db.transaction(STORE, 'readwrite')
      await requestResult(tx.objectStore(STORE).clear())
    } catch {
      // 内存已清，页面可以继续。
    }
  }

  async exportJsonl(): Promise<string> {
    const { reviews } = await this.readAll()
    return reviews.map((review) => JSON.stringify(review)).join('\n') + (reviews.length > 0 ? '\n' : '')
  }

  /** 测试/退出时释放连接；生产里页面生命周期内复用同一个连接。 */
  async close(): Promise<void> {
    const db = await this.#db
    db?.close()
    this.#db = null
  }

  async importJsonl(text: string): Promise<{ imported: number; skipped: number; invalid: number }> {
    const existing = await this.readAll()
    const ids = new Set(existing.reviews.map((review) => review.id))
    let imported = 0
    let skipped = 0
    let invalid = 0

    for (const line of text.split(/\r?\n/)) {
      if (line.trim() === '') continue
      let value: unknown
      try {
        value = JSON.parse(line)
      } catch {
        invalid += 1
        continue
      }
      if (!isReview(value) || parseCardKey(value.card) === null) {
        invalid += 1
        continue
      }
      if (ids.has(value.id)) {
        skipped += 1
        continue
      }
      await this.append(value)
      ids.add(value.id)
      imported += 1
    }

    return { imported, skipped, invalid }
  }
}
